import React, { useEffect } from 'react';
import { Form, Input, Select, Button, Card, Typography, Space, Spin, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useDevice, useCreateDevice, useUpdateDevice } from '@/hooks/useDevices';
import { useCompanies } from '@/hooks/useCompanies';
import { extractErrorMessage } from '@/utils/helpers';

const { Title } = Typography;
const { TextArea } = Input;

const DeviceForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const { data: device, isLoading: isLoadingDevice } = useDevice(id || '');
  const { data: companiesData } = useCompanies();
  const createDevice = useCreateDevice();
  const updateDevice = useUpdateDevice();

  useEffect(() => {
    if (device) {
      form.setFieldsValue(device);
    }
  }, [device, form]);

  const onFinish = async (values: any) => {
    try {
      if (id) {
        await updateDevice.mutateAsync({ id, data: values });
        message.success('Dispositivo atualizado com sucesso!');
      } else {
        await createDevice.mutateAsync(values);
        message.success('Dispositivo criado com sucesso!');
      }
      navigate('/devices');
    } catch (error: any) {
      message.error(extractErrorMessage(error, 'Erro ao salvar dispositivo'));
    }
  };

  const isLoading = createDevice.isPending || updateDevice.isPending;

  return (
    <div>
      <Title level={4}>{id ? 'Editar Dispositivo' : 'Novo Dispositivo'}</Title>
      
      <Card>
        <Spin spinning={isLoadingDevice}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Form.Item
              name="rustdesk_id"
              label="ID RustDesk"
              rules={[{ required: true, message: 'Por favor, insira o ID do RustDesk' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="hostname"
              label="Hostname"
              rules={[{ required: true, message: 'Por favor, insira o hostname' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="company"
              label="Empresa"
              rules={[{ required: true, message: 'Por favor, selecione a empresa' }]}
            >
              <Select placeholder="Selecione a empresa">
                {companiesData?.results?.map((company) => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.trade_name || company.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="os_type"
              label="Sistema Operacional"
              rules={[{ required: true, message: 'Por favor, selecione o SO' }]}
            >
              <Select>
                <Select.Option value="windows">Windows</Select.Option>
                <Select.Option value="macos">macOS</Select.Option>
                <Select.Option value="linux">Linux</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="os_version"
              label="Versão do SO"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="ip_address"
              label="IP"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="mac_address"
              label="MAC Address"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="notes"
              label="Notas"
            >
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Salvar
                </Button>
                <Button onClick={() => navigate('/devices')}>
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default DeviceForm;
